<script setup lang="ts">
import { computed } from 'vue'

import type { BetaSubscriptionBill } from '@/types/beta-dashboard'

const props = defineProps<{
    bill: BetaSubscriptionBill
}>()

const status = computed(() => {
    if (props.bill.status === 'paid') return { label: 'Pago', variant: 'success' }
    if (props.bill.status === 'waiting payment') return { label: 'Pendente', variant: 'warning' }
    if (props.bill.status === 'canceled') return { label: 'Cancelado', variant: 'error' }
    return { label: 'Recusado', variant: 'error' }
})

const currencyValue = computed(() => {
    const value = String(props.bill.value ?? '').trim()
    return /^R\$/i.test(value) ? value : `R$ ${value}`
})
</script>

<template>
    <article class="beta-subscription-bill">
        <div class="beta-subscription-bill__main">
            <div>
                <small>Fatura nº {{ props.bill.id }}</small>
                <h3>{{ props.bill.product }}</h3>
            </div>
            <span :class="['beta-subscription-bill__status', `is-${status.variant}`]">
                {{ status.label }}
            </span>
        </div>

        <dl class="beta-subscription-bill__details">
            <div>
                <dt>Vencimento</dt>
                <dd>{{ props.bill.expiry || '—' }}</dd>
            </div>
            <div>
                <dt>Referência</dt>
                <dd>{{ props.bill.referer || '—' }}</dd>
            </div>
            <div>
                <dt>Valor</dt>
                <dd>{{ currencyValue }}</dd>
            </div>
        </dl>

        <a v-if="props.bill.link" class="beta-subscription-bill__action" :href="props.bill.link">
            Pagar agora
            <span aria-hidden="true">→</span>
        </a>
    </article>
</template>

<style scoped>
.beta-subscription-bill {
    display: grid;
    min-width: 0;
    grid-template-columns: minmax(150px, 1fr) minmax(250px, 1.15fr) auto;
    align-items: center;
    gap: 15px;
    border: 1px solid #e2e8f0;
    border-radius: 15px;
    background: #fff;
    padding: 12px 14px;
}

.beta-subscription-bill__main {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 9px;
}

.beta-subscription-bill__main > div {
    min-width: 0;
}

.beta-subscription-bill__main small,
.beta-subscription-bill__main h3 {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-subscription-bill__main small {
    color: #8996a7;
    font-size: 7px;
    font-weight: 800;
    text-transform: uppercase;
}

.beta-subscription-bill__main h3 {
    margin-top: 2px;
    color: #2e4561;
    font-size: 11px;
    font-weight: 850;
}

.beta-subscription-bill__status {
    flex: none;
    border-radius: 999px;
    padding: 5px 8px;
    font-size: 7px;
    font-weight: 850;
}

.beta-subscription-bill__status.is-success {
    background: #e4f7e8;
    color: #27733c;
}

.beta-subscription-bill__status.is-warning {
    background: #fff4d8;
    color: #8c6319;
}

.beta-subscription-bill__status.is-error {
    background: #fde8e8;
    color: #a74242;
}

.beta-subscription-bill__details {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.beta-subscription-bill__details dt {
    color: #909cad;
    font-size: 7px;
    font-weight: 750;
    text-transform: uppercase;
}

.beta-subscription-bill__details dd {
    margin-top: 2px;
    color: #41566f;
    font-size: 9px;
    font-weight: 800;
}

.beta-subscription-bill__action {
    display: inline-flex;
    min-height: 32px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border-radius: 10px;
    background: var(--main-color);
    padding: 0 11px;
    color: #fff;
    font-size: 8px;
    font-weight: 850;
    white-space: nowrap;
}

@media (max-width: 760px) {
    .beta-subscription-bill {
        grid-template-columns: 1fr;
    }

    .beta-subscription-bill__main {
        justify-content: space-between;
    }

    .beta-subscription-bill__action {
        justify-self: start;
    }
}
</style>
